/* eslint-disable no-nested-ternary */
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  Icon,
  Input,
  Select,
  SimpleGrid,
  Spinner,
  Stat,
  StatLabel,
  StatNumber,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useColorModeValue,
  useToast,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  FiAlertTriangle,
  FiChevronRight,
  FiExternalLink,
  FiPackage,
  FiPrinter,
  FiRefreshCw,
  FiSearch,
  FiUser,
  FiUsers,
  FiX,
} from 'react-icons/fi';
import { Link, useLocation } from 'react-router-dom';

import { HeaderUp } from '../../../components/_components_0.2/Header_0.2';
import {
  LossControlBranch,
  LossControlClassification,
  LossControlDetails,
  LossControlDetailsStatus,
  LossControlSummary,
  lossControlService,
} from '../../../services/lossControlService';
import {
  buildLossControlDisplayPath,
  LossControlDisplayMode,
} from './displayMode';
import DisplayAccessManager from './DisplayAccessManager';
import lossControlPrintStyles from './printStyles';
import {
  formatClassificationFilter,
  formatCuiabaTimestamp,
  formatExpirationDate,
  getDaysUntilExpiration,
  getExpirationPresentation,
} from './utils';

interface SelectedSubject {
  type: 'all' | 'operator' | 'unassigned';
  accessId: string | null;
  name: string;
}

const statusOptions: Array<{
  value: LossControlDetailsStatus;
  label: string;
}> = [
  { value: 'all', label: 'Todas as situações' },
  { value: 'expired', label: 'Vencidos' },
  { value: 'due_today', label: 'Vencem hoje' },
  { value: 'due_1_7', label: 'Vencem em 1 a 7 dias' },
  { value: 'due_8_15', label: 'Vencem em 8 a 15 dias' },
  { value: 'due_16_30', label: 'Vencem em 16 a 30 dias' },
  { value: 'due_after_30', label: 'Vencem após 30 dias' },
];

const LossControl: React.FC = () => {
  const location = useLocation();
  const demo = new URLSearchParams(location.search).get('demo') === '1';
  const [branches, setBranches] = useState<LossControlBranch[]>([]);
  const [branchId, setBranchId] = useState('');
  const [summary, setSummary] = useState<LossControlSummary | null>(null);
  const [summarySearch, setSummarySearch] = useState('');
  const [families, setFamilies] = useState<LossControlClassification[]>([]);
  const [groups, setGroups] = useState<LossControlClassification[]>([]);
  const [subgroups, setSubgroups] = useState<LossControlClassification[]>([]);
  const [familyCode, setFamilyCode] = useState('');
  const [groupCode, setGroupCode] = useState('');
  const [subgroupCode, setSubgroupCode] = useState('');
  const [selectedSubject, setSelectedSubject] =
    useState<SelectedSubject | null>(null);
  const [details, setDetails] = useState<LossControlDetails | null>(null);
  const [detailsStatus, setDetailsStatus] =
    useState<LossControlDetailsStatus>('all');
  const [detailsSearch, setDetailsSearch] = useState('');
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [loadingSummary, setLoadingSummary] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [error, setError] = useState('');
  const [catalogError, setCatalogError] = useState('');
  const [detailsError, setDetailsError] = useState('');
  const [displayLoading, setDisplayLoading] = useState(false);
  const [displayMode, setDisplayMode] =
    useState<LossControlDisplayMode>('operators');
  const toast = useToast();

  const cardBackground = useColorModeValue('white', 'gray.800');
  const tableBackground = useColorModeValue('white', 'gray.800');
  const subtleText = useColorModeValue('gray.600', 'gray.300');
  const selectedBackground = useColorModeValue('green.50', 'green.900');
  const unassignedBackground = useColorModeValue('orange.50', 'orange.900');

  const loadBranches = useCallback(async () => {
    setLoadingBranches(true);
    setError('');

    try {
      const branchData = await lossControlService.listBranches(demo);
      setBranches(branchData);
      setBranchId(currentBranch => currentBranch || branchData[0]?.id || '');
    } catch (requestError) {
      setError(
        'Não foi possível consultar as lojas do Controle de Perdas. Verifique a API.',
      );
      setBranches([]);
    } finally {
      setLoadingBranches(false);
    }
  }, [demo]);

  const classificationFilters = useMemo(
    () => ({
      ...(familyCode ? { familyCode } : {}),
      ...(groupCode ? { groupCode } : {}),
      ...(subgroupCode ? { subgroupCode } : {}),
    }),
    [familyCode, groupCode, subgroupCode],
  );

  const changeFamily = useCallback((nextFamilyCode: string) => {
    setFamilyCode(nextFamilyCode);
    setGroupCode('');
    setSubgroupCode('');
    setGroups([]);
    setSubgroups([]);
  }, []);

  const changeGroup = useCallback((nextGroupCode: string) => {
    setGroupCode(nextGroupCode);
    setSubgroupCode('');
    setSubgroups([]);
  }, []);

  const loadFamilies = useCallback(async () => {
    if (!branchId) {
      setFamilies([]);
      return;
    }

    setLoadingCatalog(true);
    setCatalogError('');
    try {
      setFamilies(await lossControlService.listFamilies(branchId, demo));
    } catch (requestError) {
      setFamilies([]);
      setCatalogError('Não foi possível carregar as famílias desta loja.');
    } finally {
      setLoadingCatalog(false);
    }
  }, [branchId, demo]);

  const loadGroups = useCallback(async () => {
    if (!branchId || !familyCode) {
      setGroups([]);
      return;
    }

    setLoadingCatalog(true);
    setCatalogError('');
    try {
      setGroups(
        await lossControlService.listGroups(branchId, familyCode, demo),
      );
    } catch (requestError) {
      setGroups([]);
      setCatalogError('Não foi possível carregar os grupos desta família.');
    } finally {
      setLoadingCatalog(false);
    }
  }, [branchId, demo, familyCode]);

  const loadSubgroups = useCallback(async () => {
    if (!branchId || !familyCode || !groupCode) {
      setSubgroups([]);
      return;
    }

    setLoadingCatalog(true);
    setCatalogError('');
    try {
      setSubgroups(
        await lossControlService.listSubgroups(
          branchId,
          familyCode,
          groupCode,
          demo,
        ),
      );
    } catch (requestError) {
      setSubgroups([]);
      setCatalogError('Não foi possível carregar os subgrupos deste grupo.');
    } finally {
      setLoadingCatalog(false);
    }
  }, [branchId, demo, familyCode, groupCode]);

  const loadSummary = useCallback(async () => {
    if (!branchId) {
      setSummary(null);
      return;
    }

    setLoadingSummary(true);
    setError('');

    try {
      const response = await lossControlService.listSummary(
        { branchId, horizonDays: 90, ...classificationFilters },
        demo,
      );
      setSummary(response);
    } catch (requestError) {
      setSummary(null);
      setError(
        'Não foi possível montar o resumo por operador. Verifique a integração com o GesCooper.',
      );
    } finally {
      setLoadingSummary(false);
    }
  }, [branchId, classificationFilters, demo]);

  const loadDetails = useCallback(async () => {
    if (!selectedSubject || !branchId) {
      setDetails(null);
      return;
    }

    setLoadingDetails(true);
    setDetailsError('');

    try {
      const filters = {
        branchId,
        horizonDays: 90,
        status: detailsStatus,
        ...classificationFilters,
      };
      let response: LossControlDetails;
      if (selectedSubject.type === 'all') {
        response = await lossControlService.getAllDetails(filters, demo);
      } else if (
        selectedSubject.type === 'operator' &&
        selectedSubject.accessId
      ) {
        response = await lossControlService.getOperatorDetails(
          selectedSubject.accessId,
          filters,
          demo,
        );
      } else {
        response = await lossControlService.getUnassignedDetails(filters, demo);
      }
      setDetails(response);
    } catch (requestError) {
      setDetails(null);
      setDetailsError(
        'Não foi possível consultar os produtos selecionados. Tente novamente.',
      );
    } finally {
      setLoadingDetails(false);
    }
  }, [branchId, classificationFilters, demo, detailsStatus, selectedSubject]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);

  useEffect(() => {
    loadFamilies();
  }, [loadFamilies]);

  useEffect(() => {
    loadGroups();
  }, [loadGroups]);

  useEffect(() => {
    loadSubgroups();
  }, [loadSubgroups]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  useEffect(() => {
    loadDetails();
  }, [loadDetails]);

  const visibleOperators = useMemo(() => {
    if (!summary) return [];
    const normalizedSearch = summarySearch.trim().toLocaleLowerCase();
    if (!normalizedSearch) return summary.operators;

    return summary.operators.filter(operator =>
      operator.name.toLocaleLowerCase().includes(normalizedSearch),
    );
  }, [summary, summarySearch]);

  const visibleDetailItems = useMemo(() => {
    if (!details) return [];
    const normalizedSearch = detailsSearch.trim().toLocaleLowerCase();
    if (!normalizedSearch) return details.items;

    return details.items.filter(item =>
      [
        item.productName,
        item.productCode,
        String(item.productId),
        item.ean || '',
        item.lot,
        item.familyName,
        item.groupName,
        item.subgroupName || '',
      ].some(value => value.toLocaleLowerCase().includes(normalizedSearch)),
    );
  }, [details, detailsSearch]);

  const selectSubject = (subject: SelectedSubject) => {
    setSelectedSubject(subject);
    setDetails(null);
    setDetailsStatus('all');
    setDetailsSearch('');
  };

  const selectBranch = (nextBranchId: string) => {
    setBranchId(nextBranchId);
    setSelectedSubject(null);
    setDetails(null);
    setDetailsStatus('all');
    setDetailsSearch('');
    setFamilyCode('');
    setGroupCode('');
    setSubgroupCode('');
    setGroups([]);
    setSubgroups([]);
    setCatalogError('');
  };

  const refreshData = async () => {
    await loadSummary();
    if (selectedSubject) await loadDetails();
  };

  const openDisplay = useCallback(async () => {
    if (!branchId) {
      toast({
        title: 'Selecione uma loja antes de abrir o painel.',
        status: 'warning',
      });
      return;
    }

    const displayWindow = window.open('', '_blank');
    if (!displayWindow) {
      toast({
        title: 'O navegador bloqueou a nova janela.',
        description: 'Permita pop-ups do SAMASC e tente novamente.',
        status: 'warning',
      });
      return;
    }

    displayWindow.opener = null;
    setDisplayLoading(true);

    try {
      const displayToken = demo
        ? ''
        : (await lossControlService.createDisplayAccess(branchId)).token;
      const displayPath = buildLossControlDisplayPath({
        branchId,
        mode: displayMode,
        demo,
        token: displayToken,
      });

      displayWindow.location.replace(`${window.location.origin}${displayPath}`);
    } catch (requestError) {
      displayWindow.close();
      toast({
        title: 'Não foi possível gerar o acesso da TV.',
        description: 'Verifique a API e tente novamente.',
        status: 'error',
      });
    } finally {
      setDisplayLoading(false);
    }
  }, [branchId, demo, displayMode, toast]);

  const indicators = summary?.indicators;
  const updatedAt = summary?.sourceUpdatedAt || summary?.generatedAt || null;
  const selectedBranchName =
    branches.find(branch => branch.id === branchId)?.name || branchId;
  const detailsStatusLabel =
    statusOptions.find(option => option.value === detailsStatus)?.label || '-';
  const classificationFilterLabel = formatClassificationFilter({
    familyName: families.find(family => family.code === familyCode)?.name,
    groupName: groups.find(group => group.code === groupCode)?.name,
    subgroupName: subgroups.find(subgroup => subgroup.code === subgroupCode)
      ?.name,
  });

  return (
    <Box className="loss-control-page" minH="100vh" pb="10">
      <style>{lossControlPrintStyles}</style>
      <Box className="loss-control-no-print">
        <HeaderUp path="home" />
      </Box>

      <Box
        className="loss-control-content"
        maxW="1480px"
        mx="auto"
        px={{ base: 4, md: 6 }}
      >
        <Flex
          className="loss-control-no-print"
          mt="6"
          mb="5"
          align={{ base: 'flex-start', md: 'center' }}
          justify="space-between"
          direction={{ base: 'column', md: 'row' }}
          gridGap="4"
        >
          <Box>
            <Flex align="center" gridGap="3">
              <Icon as={FiAlertTriangle} color="orange.400" boxSize="7" />
              <Heading size="lg">Controle de Perdas</Heading>
              {demo && <Badge colorScheme="purple">Demonstração</Badge>}
            </Flex>
            <Text mt="1" color={subtleText}>
              Acompanhamento dos lotes por operador responsável.
            </Text>
          </Box>

          <Flex gridGap="2" wrap="wrap">
            <Select
              value={branchId}
              onChange={event => selectBranch(event.target.value)}
              minW={{ base: '100%', md: '300px' }}
              isDisabled={loadingBranches}
            >
              {!branchId && <option value="">Selecione uma loja</option>}
              {branches.map(branch => (
                <option key={branch.id} value={branch.id}>
                  {branch.name}
                </option>
              ))}
            </Select>
            <Button
              leftIcon={<FiRefreshCw />}
              variant="outline"
              onClick={refreshData}
              isLoading={loadingSummary || loadingDetails}
              isDisabled={!branchId}
            >
              Atualizar
            </Button>
            <Button
              colorScheme="green"
              leftIcon={<FiExternalLink />}
              onClick={openDisplay}
              isLoading={displayLoading}
              isDisabled={!branchId}
            >
              Abrir painel da TV
            </Button>
            <Select
              aria-label="Escolher visualização do painel da TV"
              value={displayMode}
              onChange={event => {
                setDisplayMode(event.target.value as LossControlDisplayMode);
              }}
              w={{ base: '100%', md: '210px' }}
              isDisabled={displayLoading}
            >
              <option value="operators">TV: por operador</option>
              <option value="products">TV: por produto</option>
            </Select>
            <DisplayAccessManager
              branchId={branchId}
              branchName={selectedBranchName}
              displayMode={displayMode}
              isDisabled={demo || displayLoading}
            />
          </Flex>
        </Flex>

        {error && (
          <Alert
            className="loss-control-no-print"
            status="error"
            mb="5"
            borderRadius="md"
          >
            <AlertIcon />
            <Box flex="1">
              <AlertTitle>Integração indisponível</AlertTitle>
              <AlertDescription>{error}</AlertDescription>
            </Box>
            {!demo && (
              <Button
                as={Link}
                to="/loss-control?demo=1"
                size="sm"
                colorScheme="red"
                variant="outline"
              >
                Visualizar demonstração
              </Button>
            )}
          </Alert>
        )}

        <SimpleGrid
          className="loss-control-no-print"
          columns={{ base: 2, md: 3, lg: 5 }}
          spacing="4"
          mb="5"
        >
          {[
            {
              label: 'Vencidos',
              value: indicators?.expired || 0,
              color: 'red.500',
            },
            {
              label: 'Vencem hoje',
              value: indicators?.dueToday || 0,
              color: 'red.400',
            },
            {
              label: '1 a 7 dias',
              value: indicators?.dueIn1To7Days || 0,
              color: 'orange.400',
            },
            {
              label: '8 a 30 dias',
              value:
                (indicators?.dueIn8To15Days || 0) +
                (indicators?.dueIn16To30Days || 0),
              color: 'yellow.500',
            },
            {
              label: 'Total monitorado',
              value: indicators?.total || 0,
              color: 'blue.500',
            },
          ].map(card => (
            <Stat
              key={card.label}
              bg={cardBackground}
              borderRadius="lg"
              borderWidth="1px"
              borderTopWidth="4px"
              borderTopColor={card.color}
              px="5"
              py="4"
              boxShadow="sm"
            >
              <StatLabel color={subtleText}>{card.label}</StatLabel>
              <StatNumber fontSize="3xl">{card.value}</StatNumber>
            </Stat>
          ))}
        </SimpleGrid>

        <Box
          className="loss-control-no-print"
          bg={tableBackground}
          borderRadius="lg"
          borderWidth="1px"
          boxShadow="sm"
          overflow="hidden"
          mb="6"
        >
          <Flex
            p="4"
            borderBottomWidth="1px"
            align={{ base: 'flex-start', md: 'center' }}
            justify="space-between"
            direction={{ base: 'column', md: 'row' }}
            gridGap="3"
          >
            <Box>
              <Flex align="center" gridGap="2">
                <Icon as={FiUsers} color="green.500" />
                <Heading size="md">Responsáveis da loja</Heading>
              </Flex>
              <Text color={subtleText} fontSize="sm" mt="1">
                Consulte todos os produtos ou selecione um responsável.
              </Text>
            </Box>
            <Box position="relative" w={{ base: '100%', md: '360px' }}>
              <Icon
                as={FiSearch}
                position="absolute"
                left="3"
                top="50%"
                transform="translateY(-50%)"
                color="gray.400"
              />
              <Input
                value={summarySearch}
                onChange={event => setSummarySearch(event.target.value)}
                placeholder="Buscar operador"
                pl="10"
              />
            </Box>
          </Flex>

          <Box p="4" borderBottomWidth="1px">
            <SimpleGrid columns={{ base: 1, md: 3 }} spacing="3">
              <Select
                aria-label="Filtrar responsáveis por família"
                value={familyCode}
                onChange={event => changeFamily(event.target.value)}
                isDisabled={!branchId || loadingCatalog}
              >
                <option value="">Todas as famílias</option>
                {families.map(family => (
                  <option key={family.code} value={family.code}>
                    {family.name}
                  </option>
                ))}
              </Select>
              <Select
                aria-label="Filtrar responsáveis por grupo"
                value={groupCode}
                onChange={event => changeGroup(event.target.value)}
                isDisabled={!familyCode || loadingCatalog}
              >
                <option value="">Todos os grupos</option>
                {groups.map(group => (
                  <option key={group.code} value={group.code}>
                    {group.name}
                  </option>
                ))}
              </Select>
              <Select
                aria-label="Filtrar responsáveis por subgrupo"
                value={subgroupCode}
                onChange={event => setSubgroupCode(event.target.value)}
                isDisabled={!groupCode || loadingCatalog}
              >
                <option value="">Todos os subgrupos</option>
                {subgroups.map(subgroup => (
                  <option key={subgroup.code} value={subgroup.code}>
                    {subgroup.name}
                  </option>
                ))}
              </Select>
            </SimpleGrid>
            {catalogError && (
              <Alert status="warning" mt="3" borderRadius="md">
                <AlertIcon />
                <AlertDescription>{catalogError}</AlertDescription>
              </Alert>
            )}
          </Box>

          {loadingSummary || loadingBranches ? (
            <Flex minH="260px" align="center" justify="center">
              <Spinner size="xl" color="green.400" />
            </Flex>
          ) : !summary ? (
            <Flex minH="260px" align="center" justify="center">
              <Text color={subtleText}>Selecione uma loja para continuar.</Text>
            </Flex>
          ) : (
            <Box overflowX="auto">
              <Table variant="simple">
                <Thead>
                  <Tr>
                    <Th>Responsável</Th>
                    <Th isNumeric>Vencidos</Th>
                    <Th isNumeric>Hoje</Th>
                    <Th isNumeric>1 a 7 dias</Th>
                    <Th isNumeric>8 a 30 dias</Th>
                    <Th isNumeric>Total</Th>
                    <Th />
                  </Tr>
                </Thead>
                <Tbody>
                  <Tr
                    bg={
                      selectedSubject?.type === 'all'
                        ? selectedBackground
                        : undefined
                    }
                    cursor="pointer"
                    _hover={{ bg: selectedBackground }}
                    onClick={() => {
                      selectSubject({
                        type: 'all',
                        accessId: null,
                        name: 'Todos os produtos',
                      });
                    }}
                  >
                    <Td minW="260px">
                      <Flex align="center" gridGap="3">
                        <Icon as={FiPackage} color="blue.500" />
                        <Box>
                          <Text fontWeight="semibold">Todos os produtos</Text>
                          <Text fontSize="xs" color={subtleText}>
                            Com e sem responsável
                          </Text>
                        </Box>
                      </Flex>
                    </Td>
                    <Td isNumeric>{summary.indicators.expired}</Td>
                    <Td isNumeric>{summary.indicators.dueToday}</Td>
                    <Td isNumeric>{summary.indicators.dueIn1To7Days}</Td>
                    <Td isNumeric>
                      {summary.indicators.dueIn8To15Days +
                        summary.indicators.dueIn16To30Days}
                    </Td>
                    <Td isNumeric fontWeight="bold">
                      {summary.indicators.total}
                    </Td>
                    <Td textAlign="right">
                      <Icon as={FiChevronRight} color="gray.400" />
                    </Td>
                  </Tr>

                  {visibleOperators.map(operator => {
                    const selected =
                      selectedSubject?.type === 'operator' &&
                      selectedSubject.accessId === operator.accessId;
                    return (
                      <Tr
                        key={operator.accessId}
                        bg={selected ? selectedBackground : undefined}
                        cursor="pointer"
                        _hover={{ bg: selectedBackground }}
                        onClick={() => {
                          selectSubject({
                            type: 'operator',
                            accessId: operator.accessId,
                            name: operator.name,
                          });
                        }}
                      >
                        <Td minW="260px">
                          <Flex align="center" gridGap="3">
                            <Icon as={FiUser} color="green.500" />
                            <Box>
                              <Text fontWeight="semibold">{operator.name}</Text>
                              <Text fontSize="xs" color={subtleText}>
                                Operador
                              </Text>
                            </Box>
                          </Flex>
                        </Td>
                        <Td isNumeric>{operator.indicators.expired}</Td>
                        <Td isNumeric>{operator.indicators.dueToday}</Td>
                        <Td isNumeric>{operator.indicators.dueIn1To7Days}</Td>
                        <Td isNumeric>
                          {operator.indicators.dueIn8To15Days +
                            operator.indicators.dueIn16To30Days}
                        </Td>
                        <Td isNumeric fontWeight="bold">
                          {operator.indicators.total}
                        </Td>
                        <Td textAlign="right">
                          <Icon as={FiChevronRight} color="gray.400" />
                        </Td>
                      </Tr>
                    );
                  })}

                  {summary.unassigned.visible && (
                    <Tr
                      bg={
                        selectedSubject?.type === 'unassigned'
                          ? unassignedBackground
                          : undefined
                      }
                      cursor="pointer"
                      _hover={{ bg: unassignedBackground }}
                      onClick={() => {
                        selectSubject({
                          type: 'unassigned',
                          accessId: null,
                          name: summary.unassigned.label,
                        });
                      }}
                    >
                      <Td minW="260px">
                        <Flex align="center" gridGap="3">
                          <Icon as={FiAlertTriangle} color="orange.500" />
                          <Box>
                            <Text fontWeight="semibold">
                              {summary.unassigned.label}
                            </Text>
                            <Text fontSize="xs" color={subtleText}>
                              Requer configuração de responsabilidade
                            </Text>
                          </Box>
                        </Flex>
                      </Td>
                      <Td isNumeric>{summary.unassigned.indicators.expired}</Td>
                      <Td isNumeric>
                        {summary.unassigned.indicators.dueToday}
                      </Td>
                      <Td isNumeric>
                        {summary.unassigned.indicators.dueIn1To7Days}
                      </Td>
                      <Td isNumeric>
                        {summary.unassigned.indicators.dueIn8To15Days +
                          summary.unassigned.indicators.dueIn16To30Days}
                      </Td>
                      <Td isNumeric fontWeight="bold">
                        {summary.unassigned.indicators.total}
                      </Td>
                      <Td textAlign="right">
                        <Icon as={FiChevronRight} color="gray.400" />
                      </Td>
                    </Tr>
                  )}
                </Tbody>
              </Table>
            </Box>
          )}

          <Flex
            px="4"
            py="3"
            borderTopWidth="1px"
            justify="space-between"
            wrap="wrap"
          >
            <Text fontSize="sm" color={subtleText}>
              {summary?.totalOperators || 0} operador
              {summary?.totalOperators === 1 ? '' : 'es'} com responsabilidade
            </Text>
            <Text fontSize="sm" color={subtleText}>
              Última atualização:{' '}
              {updatedAt ? formatCuiabaTimestamp(updatedAt) : '-'}
            </Text>
          </Flex>
        </Box>

        {selectedSubject && (
          <Box
            className="loss-control-report"
            bg={tableBackground}
            borderRadius="lg"
            borderWidth="1px"
            boxShadow="sm"
            overflow="hidden"
          >
            <Flex
              className="loss-control-report-header"
              p="4"
              borderBottomWidth="1px"
              justify="space-between"
              align={{ base: 'flex-start', lg: 'center' }}
              direction={{ base: 'column', lg: 'row' }}
              gridGap="4"
            >
              <Box>
                <Flex align="center" gridGap="2">
                  <Heading size="md">
                    Relatório de {selectedSubject.name}
                  </Heading>
                  {selectedSubject.type === 'unassigned' && (
                    <Badge
                      className="loss-control-report-badge"
                      colorScheme="orange"
                    >
                      Pendente
                    </Badge>
                  )}
                  {selectedSubject.type === 'all' && (
                    <Badge
                      className="loss-control-report-badge"
                      colorScheme="blue"
                    >
                      Visão geral
                    </Badge>
                  )}
                </Flex>
                <Text color={subtleText} fontSize="sm" mt="1">
                  {details?.totalUniqueLots || 0} lote
                  {details?.totalUniqueLots === 1 ? '' : 's'} no total
                </Text>
                <Text className="loss-control-print-meta" display="none">
                  Loja: {selectedBranchName} | Classificação:{' '}
                  {classificationFilterLabel} | Situação: {detailsStatusLabel} |
                  Atualizado:{' '}
                  {updatedAt ? formatCuiabaTimestamp(updatedAt) : '-'}
                </Text>
              </Box>

              <Flex gridGap="2" wrap="wrap" w={{ base: '100%', lg: 'auto' }}>
                <Box
                  className="loss-control-report-controls"
                  display="flex"
                  gridGap="2"
                  flexWrap="wrap"
                >
                  <Button
                    leftIcon={<FiPrinter />}
                    variant="outline"
                    onClick={() => window.print()}
                    isDisabled={!details || details.items.length === 0}
                  >
                    Imprimir relatório
                  </Button>
                  <Button
                    leftIcon={<FiX />}
                    variant="ghost"
                    onClick={() => {
                      setSelectedSubject(null);
                      setDetails(null);
                    }}
                  >
                    Fechar
                  </Button>
                </Box>
              </Flex>
            </Flex>

            <Box
              className="loss-control-report-controls"
              p="4"
              borderBottomWidth="1px"
            >
              <SimpleGrid columns={{ base: 1, md: 2, xl: 4 }} spacing="3">
                <Select
                  aria-label="Filtrar relatório por família"
                  value={familyCode}
                  onChange={event => changeFamily(event.target.value)}
                  isDisabled={!branchId || loadingCatalog}
                >
                  <option value="">Todas as famílias</option>
                  {families.map(family => (
                    <option key={family.code} value={family.code}>
                      {family.name}
                    </option>
                  ))}
                </Select>
                <Select
                  aria-label="Filtrar relatório por grupo"
                  value={groupCode}
                  onChange={event => changeGroup(event.target.value)}
                  isDisabled={!familyCode || loadingCatalog}
                >
                  <option value="">Todos os grupos</option>
                  {groups.map(group => (
                    <option key={group.code} value={group.code}>
                      {group.name}
                    </option>
                  ))}
                </Select>
                <Select
                  aria-label="Filtrar relatório por subgrupo"
                  value={subgroupCode}
                  onChange={event => setSubgroupCode(event.target.value)}
                  isDisabled={!groupCode || loadingCatalog}
                >
                  <option value="">Todos os subgrupos</option>
                  {subgroups.map(subgroup => (
                    <option key={subgroup.code} value={subgroup.code}>
                      {subgroup.name}
                    </option>
                  ))}
                </Select>
                <Select
                  aria-label="Filtrar relatório por situação"
                  value={detailsStatus}
                  onChange={event => {
                    setDetailsStatus(
                      event.target.value as LossControlDetailsStatus,
                    );
                  }}
                >
                  {statusOptions.map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </Select>
              </SimpleGrid>
              {catalogError && (
                <Alert status="warning" mt="3" borderRadius="md">
                  <AlertIcon />
                  <AlertDescription>{catalogError}</AlertDescription>
                </Alert>
              )}
            </Box>

            {detailsError && (
              <Alert status="error" m="4" borderRadius="md">
                <AlertIcon />
                <AlertDescription>{detailsError}</AlertDescription>
              </Alert>
            )}

            {details && details.classifications.length > 0 && (
              <Flex
                className="loss-control-report-summary-badges"
                px="4"
                pt="4"
                gridGap="2"
                wrap="wrap"
              >
                {details.classifications.map(family => (
                  <Badge
                    key={family.familyCode}
                    colorScheme="green"
                    px="3"
                    py="1"
                    borderRadius="full"
                  >
                    {family.familyName}: {family.totalUniqueLots}
                  </Badge>
                ))}
              </Flex>
            )}

            <Box
              className="loss-control-report-controls"
              p="4"
              borderBottomWidth="1px"
            >
              <Box position="relative">
                <Icon
                  as={FiSearch}
                  position="absolute"
                  left="3"
                  top="50%"
                  transform="translateY(-50%)"
                  color="gray.400"
                />
                <Input
                  value={detailsSearch}
                  onChange={event => setDetailsSearch(event.target.value)}
                  placeholder="Buscar produto, código, EAN, lote ou classificação"
                  pl="10"
                />
              </Box>
            </Box>

            {loadingDetails ? (
              <Flex minH="260px" align="center" justify="center">
                <Spinner size="xl" color="green.400" />
              </Flex>
            ) : visibleDetailItems.length === 0 ? (
              <Flex
                minH="260px"
                align="center"
                justify="center"
                direction="column"
              >
                <Icon as={FiSearch} boxSize="8" color="gray.400" mb="3" />
                <Text fontWeight="medium">Nenhum lote encontrado</Text>
                <Text color={subtleText} fontSize="sm">
                  Ajuste a classificação, a situação ou a busca do relatório.
                </Text>
              </Flex>
            ) : (
              <Box overflowX="auto">
                <Table className="loss-control-report-table" variant="simple">
                  <Thead>
                    <Tr>
                      <Th>Situação</Th>
                      <Th>Produto</Th>
                      <Th>Classificação</Th>
                      <Th>Lote</Th>
                      <Th isNumeric>Quantidade</Th>
                      <Th>Validade</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {visibleDetailItems.map(item => {
                      const presentation = getExpirationPresentation(
                        getDaysUntilExpiration(item.expiryDate),
                      );
                      return (
                        <Tr key={item.id}>
                          <Td
                            borderLeftWidth="4px"
                            borderLeftColor={presentation.accent}
                          >
                            <Badge
                              className="loss-control-status-badge"
                              colorScheme={presentation.colorScheme}
                              whiteSpace="nowrap"
                            >
                              {presentation.label}
                            </Badge>
                          </Td>
                          <Td minW="260px">
                            <Text
                              className="loss-control-product-name"
                              fontWeight="semibold"
                            >
                              {item.productName}
                            </Text>
                            <Text
                              className="loss-control-product-details"
                              fontSize="xs"
                              color={subtleText}
                            >
                              Cód. {item.productCode}
                              {item.ean && (
                                <span className="loss-control-ean">
                                  {' '}
                                  | EAN {item.ean}
                                </span>
                              )}
                            </Text>
                          </Td>
                          <Td minW="240px">
                            <Text
                              className="loss-control-classification-main"
                              fontSize="sm"
                              fontWeight="medium"
                            >
                              {item.familyName} / {item.groupName}
                            </Text>
                            {item.subgroupName && (
                              <Text
                                className="loss-control-classification-subgroup"
                                fontSize="xs"
                                color={subtleText}
                              >
                                {item.subgroupName}
                              </Text>
                            )}
                          </Td>
                          <Td fontWeight="medium">{item.lot}</Td>
                          <Td isNumeric>
                            {item.quantity.toLocaleString('pt-BR')}{' '}
                            {item.unit || ''}
                          </Td>
                          <Td whiteSpace="nowrap" fontWeight="semibold">
                            {formatExpirationDate(item.expiryDate)}
                          </Td>
                        </Tr>
                      );
                    })}
                  </Tbody>
                </Table>
              </Box>
            )}

            <Flex
              className="loss-control-report-footer"
              px="4"
              py="3"
              borderTopWidth="1px"
              justify="space-between"
              wrap="wrap"
            >
              <Text fontSize="sm" color={subtleText}>
                {visibleDetailItems.length} lote
                {visibleDetailItems.length === 1 ? '' : 's'} exibido
                {visibleDetailItems.length === 1 ? '' : 's'}
              </Text>
              <Box textAlign={{ base: 'left', md: 'right' }}>
                <Text fontSize="sm" color={subtleText}>
                  Classificação: {classificationFilterLabel}
                </Text>
                <Text fontSize="sm" color={subtleText}>
                  Situação: {detailsStatusLabel}
                </Text>
              </Box>
            </Flex>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default LossControl;
